import { useMemo, useState } from "react";
import BranchFormModal from "@/pages/admin/branches/components/BranchFormModal";
import { useBranches } from "@/hooks/useBranches";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { Branch } from "@/types";

interface SocialLink {
  key: string;
  icon: string;
  label: string;
  url: string | null;
}

function socials(branch: Branch): SocialLink[] {
  return [
    { key: "whatsapp", icon: "ri-whatsapp-line", label: "WhatsApp", url: branch.whatsapp },
    { key: "facebook", icon: "ri-facebook-circle-line", label: "Facebook", url: branch.facebook },
    { key: "instagram", icon: "ri-instagram-line", label: "Instagram", url: branch.instagram },
    { key: "tiktok", icon: "ri-tiktok-line", label: "TikTok", url: branch.tiktok },
    { key: "youtube", icon: "ri-youtube-line", label: "YouTube", url: branch.youtube },
  ];
}

export default function AdminBranchesPage() {
  const toast = useToast();
  const { profile } = useAuth();
  const { branches, loading, error, reload } = useBranches();

  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Branch | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return branches;
    return branches.filter((branch) => {
      const haystack = `${branch.name} ${branch.code ?? ""} ${branch.address ?? ""}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [branches, search]);

  const activeCount = branches.filter((branch) => branch.active).length;

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (branch: Branch) => {
    setEditing(branch);
    setFormOpen(true);
  };

  const handleToggleActive = async (branch: Branch) => {
    setTogglingId(branch.id);
    const { error: updateError } = await supabase
      .from("branches")
      .update({ active: !branch.active, updated_at: new Date().toISOString() })
      .eq("id", branch.id);
    setTogglingId(null);

    if (updateError) {
      toast.error("No se pudo actualizar el estado de la sucursal.");
      return;
    }

    await logAudit({
      userId: profile?.id ?? null,
      userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
      action: "editar",
      entity: "branches",
      entityId: branch.id,
      branchId: branch.id,
      oldValue: { active: branch.active },
      newValue: { active: !branch.active },
    });

    toast.success(branch.active ? "Sucursal desactivada." : "Sucursal activada.");
    reload();
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Sucursales</h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            Administra las sucursales del negocio, sus datos fiscales y su numeración de facturas.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-add-line text-base"></i>
          Nueva sucursal
        </button>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-building-2-line text-base"></i>
            </span>
            <div>
              <p className="font-heading text-lg font-bold leading-none text-foreground-950">
                {branches.length}
              </p>
              <p className="mt-1 text-[11px] text-foreground-500">Total</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-800">
              <i className="ri-checkbox-circle-line text-base"></i>
            </span>
            <div>
              <p className="font-heading text-lg font-bold leading-none text-foreground-950">
                {activeCount}
              </p>
              <p className="mt-1 text-[11px] text-foreground-500">Activas</p>
            </div>
          </div>
        </div>

        <div className="relative w-full lg:w-72">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar sucursal..."
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={reload}
            className="whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700"
          >
            Reintentar
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg"></i>
          Cargando sucursales...
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-14 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-building-2-line text-2xl"></i>
          </span>
          <p className="text-sm font-semibold text-foreground-800">Sin sucursales</p>
          <p className="max-w-sm text-xs text-foreground-500">
            Crea la primera sucursal para comenzar a operar el POS, inventario y facturación.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {filtered.map((branch) => {
            const links = socials(branch).filter((link) => link.url);
            return (
              <div
                key={branch.id}
                className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                      <i className="ri-store-2-line text-xl"></i>
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-semibold text-foreground-950">
                          {branch.name}
                        </p>
                        {branch.code ? (
                          <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary-800">
                            {branch.code}
                          </span>
                        ) : null}
                      </div>
                      <span
                        className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          branch.active
                            ? "bg-accent-100 text-accent-900"
                            : "bg-secondary-200 text-secondary-900"
                        }`}
                      >
                        {branch.active ? "Activa" : "Inactiva"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  <div className="flex items-start gap-2 text-xs text-foreground-600">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-foreground-400">
                      <i className="ri-map-pin-line"></i>
                    </span>
                    <span className="min-w-0 break-words">{branch.address || "Sin dirección"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground-600">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center text-foreground-400">
                      <i className="ri-phone-line"></i>
                    </span>
                    <span className="truncate">{branch.phone || "Sin teléfono"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground-600">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center text-foreground-400">
                      <i className="ri-file-list-line"></i>
                    </span>
                    <span className="truncate">RNC: {branch.rnc || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground-600">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center text-foreground-400">
                      <i className="ri-mail-line"></i>
                    </span>
                    <span className="truncate">{branch.email || "Sin correo"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground-600">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center text-foreground-400">
                      <i className="ri-time-line"></i>
                    </span>
                    <span className="truncate">{branch.schedule || "Sin horario"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground-600">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center text-foreground-400">
                      <i className="ri-receipt-line"></i>
                    </span>
                    <span className="truncate">
                      Facturas: {branch.invoice_prefix || "—"}-{branch.invoice_sequence ?? 1}
                    </span>
                  </div>
                </div>

                {links.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {links.map((link) => (
                      <span
                        key={link.key}
                        className="flex items-center gap-1.5 rounded-full border border-background-200 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-600"
                      >
                        <i className={`${link.icon} text-sm`}></i>
                        {link.label}
                      </span>
                    ))}
                  </div>
                ) : null}

                <div className="flex flex-wrap items-center gap-2 border-t border-background-200 pt-4">
                  <button
                    type="button"
                    onClick={() => openEdit(branch)}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs font-semibold text-foreground-800 transition-colors hover:bg-background-200"
                  >
                    <i className="ri-edit-2-line text-sm"></i>
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleActive(branch)}
                    disabled={togglingId === branch.id}
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-60 ${
                      branch.active
                        ? "border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100"
                        : "border-accent-300 bg-accent-50 text-accent-900 hover:bg-accent-100"
                    }`}
                  >
                    {togglingId === branch.id ? (
                      <i className="ri-loader-4-line animate-spin text-sm"></i>
                    ) : (
                      <i className={`${branch.active ? "ri-close-circle-line" : "ri-checkbox-circle-line"} text-sm`}></i>
                    )}
                    {branch.active ? "Desactivar" : "Activar"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <BranchFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={reload}
        branch={editing}
      />
    </div>
  );
}